import { AddToFavouritesGroup } from "../../../components/addToFavouritesButtons/AddToFavouritesGroup";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import { useCheckoutSuccessPageData } from "./lib/useCheckoutSuccessPageData";
import { useFocusedNavigation } from "../../../hooks/useFocusedNavigation";
import { CheckoutPageLoading } from "./lib/CheckoutPageLoading";
import { CheckoutPageLayout } from "./lib/CheckoutPageLayout";
import { Icon, OrderStage, SpinnerLegacy } from "../../../components";
import {
  calculateCardPaymentDeposit,
  formatCurrency,
  pluralize,
  sanitizeEmailArray,
} from "@sellar-io/common";
import { ComponentProps, ReactNode, useEffect, useRef, useState } from "react";
import { useIsDesktop } from "./lib/useIsDesktop";
import {
  DiscoveryV2RecentlyViewedStoreState,
  notRecentlyViewedExpired,
  useDiscoveryV2RecentlyViewedStore,
} from "../../discovery/v2/DiscoveryV2RecentlyViewedStore";
import { DiscoveryHorizontalList } from "../../discovery/v2/components/DiscoveryHorizontalList";
import {
  CheckoutPaymentStatusResponseType,
  CompanyCreditInfoResponseType,
  OrderPaymentType,
  BuyerProductGetResponseType,
  SupplierRecentlyAddedNewListResponseType,
  OrderGetResponseType,
  DiscoveryV2ListResponseType,
} from "@sellar-io/api-v2/types";
import { useQuery } from "@tanstack/react-query";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { ServerStateKeys } from "../../../hooks/api/v2/ServerStateKeys";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultQueryOptions,
} from "../../../hooks/api/v2/apiV2Request";
import { useNavigate, useLocation } from "react-router";
import {
  DISCOVER,
  DISCOVER_DISTRIBUTION,
  ORDERS,
  ORDER_BUYER,
  ORDER_SUCCESS_BUYER,
} from "../../../constants/routes";
import { useDiscoveryV2ProductList } from "../../../hooks/api/v2/discovery/useDiscoveryV2ProductList";
import { defaultLondonLocation } from "../../../components/map/SellarMap.shared";
import { useSupplierListByDistance } from "../../../hooks/api/v2/supplier/useSupplierListByDistance";
import { AddToListButton } from "../../../components/supplierAddToList/SupplierAddToListCard";
import { useBuyerRelationshipList } from "../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { useDiscoveryPreOrdersProducts } from "../../../hooks/api/v2/discovery/useDiscoveryPreOrders";
import dayjs from "dayjs";
import { useBuyerRelationshipCount } from "../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipCount";
import { useSellarUser } from "../../../hooks";
import { twMerge } from "tailwind-merge";
import {
  DiscoveryListProductCard,
  DiscoveryListProductCardProps,
} from "../../discovery/v2/DiscoveryListProductCard";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { DiscoverySupplierCardCompact } from "../../discovery/v2/components/DiscoverySupplierCardCompact";
import { ImageWithFallback } from "../../../components/image/imageWithFallback";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { NotificationSettingsPromptDialog } from "../../settings/notificationSettings/NotificationSettingsPromptDialog";
import { usePaymentIntent } from "../../../hooks/api/v2/paymentIntent/usePaymentIntent";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { StripeProvider } from "./lib/StripeProvider";
import { CheckoutDialog } from "./lib/CheckoutDialog";
import { Alert } from "../../../components/alert/Alert";
import {
  CardElement,
  ElementsConsumer,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useOrderCancel } from "../../../hooks/api/v2/order/v2/useOrderCancel";
import { useToast } from "../../../components/toast/useToast.shared";
import { Toast } from "../../../components/toast/Toast";
import { Mixpanel } from "../../../utils/mixpanel";
import { PayWithSellarCreditButton } from "./lib/PayWithSellarCreditButton";
import { useLegalEntityCompanyCreditInfo } from "../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { useDiscoveryDistributionSupplierList } from "../../../hooks/api/v2/discovery/useDiscoveryDistributionSupplierList";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { DiscoveryV2DistributionSupplierCard } from "../../discovery/v2/distribution/DiscoveryV2DistributionSupplierCard";
import {
  Button,
  Dialog,
  H4,
  InputCheckbox,
  ProfilePicture,
  DialogContent,
  DialogCTA,
} from "@sellar-io/components";
import { useOpenDistributionOrderList } from "../../../hooks/api/v2/order/v2/useOpenDistributionOrderList";
import { useDiscoveryV2DistributionTracking } from "../../discovery/v2/distribution/useDiscoveryV2DistributionTracking";
import { useDiscoverySpiritsProducts } from "../../../hooks/api/v2/discovery/useDiscoverySpiritsProducts";
import { useSupplierAddToList } from "~/hooks/useSupplierAddToList";
import { mapToPaymentMethod } from "./lib/mapToPaymentMethod";
import { useParamAsNumber } from "~/hooks/useParamAsNumber";

type HandleOnClickProductParams = {
  productId: number;
  contentTitle: string;
  supplierUrlId: string;
};

export type HandleOnClickSupplierParams = {
  contentTitle: string;
  supplierUrlId: string;
  supplierName: string;
};

const PRODUCTS_PER_SLIDER = 15;
const SUPPLIERS_PER_SLIDER = 15;

export const CheckoutV2SuccessPageWithFeed = () => {
  const orderIdParam = useParamAsNumber(ORDER_SUCCESS_BUYER, "orderId");
  useFocusedNavigation();
  const { onTrackSupplierClick, onTrackAddMoreClick } =
    useDiscoveryV2DistributionTracking();
  const [
    openDistributionInformationDialog,
    setOpenDistributionInformationDialog,
  ] = useState(false);
  const [
    canShowDistributionInformationDialogAgain,
    setCanShowDistributionInformationDialogAgain,
  ] = useLocalStorage<boolean>(
    "canShowDistributionInformationDialogAgain",
    true,
  );
  const [destinationUrl, setDestinationUrl] = useState("");
  const navigate = useNavigate();
  const { trackFeedContent } = useTrackEndOfCheckoutFeed();
  const {
    loading,
    supplierName,
    supplierLogoUrl,
    orderTotal,
    orderStatus,
    orderItems,
    orderVatPercentage,
    orderId,
    paymentIntentId,
    paymentStatus,
    paymentType,
    stripeAccountId,
    retailerBusinessId,
    supplierId,
    twoOrder,
    fulfillmentType,
    deliveryPlannedAt,
    hasPaymentLink,
  } = useCheckoutSuccessPageData({ orderId: orderIdParam });
  const purchasedProductIds = orderItems.map(
    (orderItem) => orderItem.productId,
  );
  const recentlyViewed = useDiscoveryV2RecentlyViewedStore(
    (state) => state.recentlyViewed,
  )
    .filter((product) => notRecentlyViewedExpired(product.expiry))
    .filter((product) => !purchasedProductIds.includes(product.id));
  const { feed, creditLimitQuote } = useEndOfCheckoutFeed({
    recentlyViewed,
    fulfillmentType,
  });
  const { isActive: isFeatureFlagActive } = useRemoteFeatureFlags();

  const navTitle = supplierName
    ? `Checkout with ${supplierName}`
    : `Order #${orderId}`;

  if (loading) {
    return <CheckoutPageLoading title={navTitle} description="" />;
  }

  const handleProductClick = ({
    productId,
    contentTitle,
    supplierUrlId,
  }: HandleOnClickProductParams) => {
    trackFeedContent({ title: contentTitle });
    navigate(
      `${ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlId })}?categoryId=${productId}`,
    );
  };

  const handleSupplierClick = ({
    supplierUrlId,
  }: HandleOnClickSupplierParams) => {
    navigate(ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlId }));
  };

  const handleSpiritProductClick = (params: HandleOnClickProductParams) => {
    trackFeedContent({ title: "Spirits section product" });
    navigate(
      `${ORDER_BUYER.href({ supplierIdOrUrlId: params.supplierUrlId })}?categoryId=${params.productId}`,
    );
  };

  const handleSocialIconClick = ({
    url,
    type,
  }: {
    url: string;
    type: string;
  }) => {
    trackFeedContent({ title: "Social Links", socialLinkType: type });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const canShowSellarCreditPitchWithReveal = feed.has(
    "sellar-credit-pitch-with-credit-reveal",
  );
  const canShowSellarCreditPitchWithoutReveal = feed.has(
    "sellar-credit-pitch-without-credit-reveal",
  );

  const canShowRecentlyViewedProducts = feed.has("recently-viewed");
  const canShowRecentlyAddedByMySupplierProducts = feed.has(
    "recently-added-by-my-suppliers",
  );
  const canShowRecentlyAddedProducts = feed.has("recently-added");
  const canShowDirectDeliverySuppliers = feed.has(
    "suppliers-delivering-direct",
  );
  const canShowRecommendedDirectDeliveryProducts = feed.has(
    "recommended-from-suppliers-near-you",
  );
  const canShowPreorders = feed.has("pre-orders");
  const canShowBestPricePromise = feed.has("best-price-promise");
  const canShowSocials = feed.has("link-to-socials");
  const canShowSellarDistribution = feed.has("sellar-distribution");
  const canShowSpiritsSection =
    feed.has("spirits-section") &&
    isFeatureFlagActive("spiritsSectionCheckout");

  console.log("canShowSpiritsSection", canShowSpiritsSection);
  console.log('feed.has("spirits-section")', feed.has("spirits-section"));

  return (
    <>
      <CheckoutPageLayout
        alerts={[]}
        title={navTitle}
        layout="centered"
        aside={
          <CheckoutSuccessSummary
            supplierName={supplierName}
            supplierLogoUrl={supplierLogoUrl}
            stripeAccountId={stripeAccountId}
            paymentIntentId={paymentIntentId}
            orderId={orderId}
            orderItems={orderItems}
            orderStatus={orderStatus}
            orderTotal={orderTotal}
            orderVatPercentage={orderVatPercentage}
            paymentStatus={paymentStatus}
            paymentType={paymentType}
            retailerBusinessId={retailerBusinessId}
            supplierId={supplierId}
            twoOrder={twoOrder}
            onClickBackToMarket={() => {
              const nextUrl = DISCOVER;
              if (
                canShowSellarDistribution &&
                canShowDistributionInformationDialogAgain
              ) {
                setDestinationUrl(nextUrl);
                setOpenDistributionInformationDialog(true);
                return;
              }
              navigate(nextUrl);
            }}
            hasPaymentLink={hasPaymentLink}
            onClickReviewOrEditOrder={(orderId) => {
              const nextUrl = `${ORDERS}/${orderId}?tab=All+orders`;

              if (
                canShowSellarDistribution &&
                canShowDistributionInformationDialogAgain
              ) {
                setDestinationUrl(nextUrl);
                setOpenDistributionInformationDialog(true);
                return;
              }

              navigate(nextUrl);
            }}
          />
        }
      >
        <div className="flex flex-col gap-8 px-4 pt-2 pb-8">
          {canShowSellarDistribution && (
            <div className="flex flex-col gap-8">
              <SellarDistributionOrderDisplay
                deliveryPlannedAt={deliveryPlannedAt ?? null}
                orderTotal={orderTotal}
                supplierName={supplierName}
                nbOrderItems={orderItems.length}
                orderId={orderId}
              />
              {canShowSpiritsSection && (
                <EndOfCheckoutSpiritsProducts
                  onProductClick={(params) => {
                    trackFeedContent({ title: "Spirits section product" });
                    handleSpiritProductClick(params);
                  }}
                />
              )}

              <SellarDistributionSupplierList
                onSupplierClick={(params) => {
                  onTrackSupplierClick({
                    supplierName: params.supplierName,
                    source: "end-of-checkout",
                  });
                  handleSupplierClick(params);
                }}
                onHeaderClick={() => {
                  onTrackAddMoreClick({
                    title: "Add more to your delivery clicked",
                    source: "end-of-checkout",
                  });
                  navigate(DISCOVER_DISTRIBUTION);
                }}
              />
            </div>
          )}

          {canShowSellarCreditPitchWithoutReveal && <SellarCreditPitch />}
          {canShowSellarCreditPitchWithReveal && (
            <SellarCreditPitch
              creditCheckReveal={
                <SellarCreditPitchCreditReveal
                  creditLimitQuote={creditLimitQuote}
                  onClick={() => {
                    trackFeedContent({
                      title: "Sellar Credit Pitch With Credit Limit",
                    });
                    navigate(`${DISCOVER}?showSellarCreditOnboarding=true`);
                  }}
                />
              }
            />
          )}
          {canShowRecentlyViewedProducts && (
            <EndOfCheckoutRecentlyViewedProducts
              recentlyViewed={recentlyViewed}
              onProductClick={handleProductClick}
            />
          )}
          {canShowRecentlyAddedByMySupplierProducts && (
            <EndOfCheckoutRecentlyAddedByMySupplierProducts
              onProductClick={handleProductClick}
            />
          )}
          {canShowRecentlyAddedProducts && (
            <EndOfCheckoutRecentlyAddedProducts
              onProductClick={handleProductClick}
            />
          )}
          {canShowBestPricePromise && (
            <EndOfCheckoutBestPricePromise
              onClick={() => {
                trackFeedContent({ title: "Best Price Promise" });
                window.open("https://sellar.io/best-price-promise", "_blank");
              }}
            />
          )}
          {canShowDirectDeliverySuppliers && (
            <EndOfCheckoutDirectDeliverySuppliers
              onSupplierClick={(params) => {
                trackFeedContent({ title: params.contentTitle });
                handleSupplierClick(params);
              }}
            />
          )}
          {canShowRecommendedDirectDeliveryProducts && (
            <EndOfCheckoutRecommendedDirectDeliveryProducts
              onProductClick={handleProductClick}
            />
          )}
          {canShowPreorders && (
            <EndOfCheckoutPreorderProducts
              onProductClick={handleProductClick}
            />
          )}
          {canShowSocials && (
            <EndOfCheckoutSocials onClickSocial={handleSocialIconClick} />
          )}
        </div>
      </CheckoutPageLayout>
      <SellarDistributionCheckoutInformationDialog
        open={openDistributionInformationDialog}
        canShowDialogAgain={canShowDistributionInformationDialogAgain}
        setCanShowDialogAgain={setCanShowDistributionInformationDialogAgain}
        onAddMoreOrders={() => {
          navigate(DISCOVER_DISTRIBUTION);
        }}
        onDismiss={() => {
          setOpenDistributionInformationDialog(false);
          navigate(destinationUrl);
        }}
      />
    </>
  );
};

type SellarDistributionOrderDisplayProps = {
  deliveryPlannedAt: Date | null;
  supplierName: string;
  orderTotal: number;
  nbOrderItems: number;
  orderId: number;
};

const SellarDistributionOrderDisplay = ({
  orderId,
  deliveryPlannedAt,
  supplierName,
  orderTotal,
  nbOrderItems,
}: SellarDistributionOrderDisplayProps) => {
  const { onTrackAddMoreClick } = useDiscoveryV2DistributionTracking();

  const navigate = useNavigate();
  const openDistributionOrderListQuery = useOpenDistributionOrderList(
    {
      query: deliveryPlannedAt
        ? [
            {
              type: "deliveryPlannedAt",
              modifier: "EQUALS",
              value: deliveryPlannedAt,
            } as const,
          ]
        : [],
      limit: 20,
    },
    {
      enabled: Boolean(deliveryPlannedAt),
    },
  );

  const openDistributionOrders =
    openDistributionOrderListQuery.data?.pages.flat() ?? [];

  const title = deliveryPlannedAt
    ? `Your ${dayjs(deliveryPlannedAt).format("dddd")} delivery is scheduled`
    : "Your delivery is scheduled";

  const subtitle = deliveryPlannedAt
    ? dayjs(deliveryPlannedAt).format("dddd D MMM")
    : null;

  const ordersArrivingTogether = [
    { supplierName, orderTotal, nbOrderItems, orderId },
    ...openDistributionOrders
      .filter((order) => order.id !== orderId)
      .map((order) => ({
        supplierName: order.Supplier.Business.name,
        orderTotal: order.price,
        nbOrderItems: order.OrderItems.length,
        orderId: order.id,
      })),
  ];

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[#E8D3F4] bg-[#E8D3F433] p-4">
      <div className="flex gap-4">
        <div className="flex h-14 min-h-14 w-14 min-w-14 flex-col items-center justify-center rounded-full bg-[#E8D3F4]/20">
          <div className="flex h-10 min-h-10 w-10 min-w-10 items-center justify-center rounded-full bg-[#E8D3F4]">
            <Icon
              icon="SELLAR_DISTRIBUTION_TRUCK"
              viewBox="0 0 16 16"
              className="size-6 text-[#8C23C7]"
            />
          </div>
        </div>
        <div>
          <p className="font-semibold">{title}</p>
          {subtitle && <p className="text-zinc-600">{subtitle}</p>}
        </div>
      </div>
      <hr className="border-[#E8D3F4]" />
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2">
          <Icon
            size={0.7}
            icon="PACKAGE_VARIANT_CLOSED_PLUS"
            className="relative top-0.5"
          />
          {ordersArrivingTogether.length === 1 && (
            <div className="flex flex-col gap-2">
              <p className="font-semibold text-zinc-700">1 order</p>
              <OrderArrivingTogetherItem
                supplierName={supplierName}
                nbOrderItems={nbOrderItems}
                orderTotal={orderTotal}
              />
            </div>
          )}
          {ordersArrivingTogether.length > 1 && (
            <div className="flex flex-col gap-2">
              <p className="font-semibold text-zinc-700">
                {ordersArrivingTogether.length}{" "}
                {pluralize("order", ordersArrivingTogether.length)} arriving
                together
              </p>
              {ordersArrivingTogether.map((order) => (
                <OrderArrivingTogetherItem
                  key={order.orderId}
                  supplierName={order.supplierName}
                  nbOrderItems={order.nbOrderItems}
                  orderTotal={order.orderTotal}
                />
              ))}
            </div>
          )}
        </div>
        <Button
          onClick={() => {
            onTrackAddMoreClick({
              source: "end-of-checkout",
              title: "Add more from different makers clicked",
            });
            navigate(DISCOVER_DISTRIBUTION);
          }}
          color="purple"
          className="w-full bg-[#8C23C7]"
        >
          Add More From Different makers
          <Icon icon="CHEVRON_RIGHT" />
        </Button>
        <p className="text-center text-xs text-zinc-600">
          Need more stock for this week? Order from makers delivered by Sellar
          before the cut-off to add to your scheduled delivery
        </p>
      </div>
    </div>
  );
};

export const OrderArrivingTogetherItem = ({
  supplierName,
  orderTotal,
  nbOrderItems,
  onClick,
}: {
  supplierName: string;
  orderTotal: number;
  nbOrderItems: number;
  onClick?: () => void;
}) => {
  return (
    <div
      className={twMerge("flex flex-col", onClick && "cursor-pointer")}
      onClick={onClick}
    >
      <p className="font-semibold text-zinc-700">{supplierName}</p>
      <div className="flex items-center gap-1 text-zinc-600">
        {nbOrderItems} {pluralize("item", nbOrderItems)} •{" "}
        {formatCurrency(orderTotal)}
        {onClick && (
          <>
            {" "}
            <Icon icon="CHEVRON_RIGHT" size={0.65} />
          </>
        )}
      </div>
    </div>
  );
};

export const SellarDistributionSupplierList = ({
  onSupplierClick,
  onHeaderClick,
}: {
  onSupplierClick: (params: HandleOnClickSupplierParams) => void;
  onHeaderClick: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();

  const buyerPostcode = business?.DeliveryAddress.postalCode ?? "";

  const discoveryDistributionSupplierListQuery =
    useDiscoveryDistributionSupplierList(
      {
        query: [
          {
            type: "retailerPostalCode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
        limit: SUPPLIERS_PER_SLIDER,
      },
      {
        enabled: Boolean(buyerPostcode),
      },
    );

  const discoveryDistributionSuppliers =
    discoveryDistributionSupplierListQuery.data?.pages.flat() ?? [];

  if (!buyerPostcode || discoveryDistributionSuppliers.length === 0) {
    return null;
  }

  const title = "Add more to your delivery";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onHeaderClick}
          className="flex w-fit cursor-pointer items-center justify-start text-lg font-semibold text-zinc-800"
        >
          <span>{title}</span>
          <Icon icon="CHEVRON_RIGHT" />
        </button>
        <p className="text-base text-zinc-600">
          Order from more makers delivered by Sellar
        </p>
      </div>
      <InfiniteList
        className="w-full"
        query={discoveryDistributionSupplierListQuery}
      >
        <div className="grid grid-cols-1 gap-4 overflow-hidden bg-transparent pb-4 min-[480px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
          {discoveryDistributionSupplierListQuery.data?.pages.map((page) =>
            page.map((supplier) => (
              <DiscoveryV2DistributionSupplierCard
                key={supplier.id}
                onClick={() => {
                  onSupplierClick({
                    supplierUrlId: supplier.urlId,
                    contentTitle: title,
                    supplierName: supplier.Business.name,
                  });
                }}
                imageUrl={supplier.imageUrl ?? ""}
                logoUrl={supplier.Business.logoUrl ?? ""}
                deliveryAddressCity={supplier.Business.DeliveryAddress.city}
                businessName={supplier.Business.name}
                className="w-full sm:w-50"
              />
            )),
          )}
        </div>
      </InfiniteList>
    </div>
  );
};

type SellarDistributionCheckoutInformationDialogProps = {
  open: boolean;
  canShowDialogAgain: boolean;
  setCanShowDialogAgain: (canShowDialogAgain: boolean) => void;
  onDismiss: () => void;
  onAddMoreOrders: () => void;
};

const SellarDistributionCheckoutInformationDialog = ({
  open,
  canShowDialogAgain,
  setCanShowDialogAgain,
  onDismiss,
  onAddMoreOrders,
}: SellarDistributionCheckoutInformationDialogProps) => {
  const { business } = useSellarCurrentBusiness();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const buyerPostcode = business?.DeliveryAddress.postalCode ?? "";

  const discoveryDistributionSupplierListQuery =
    useDiscoveryDistributionSupplierList(
      {
        query: [
          {
            type: "retailerPostalCode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
        limit: 3,
      },
      {
        enabled: Boolean(buyerPostcode),
      },
    );

  const suppliers = (
    discoveryDistributionSupplierListQuery.data?.pages.flat() ?? []
  ).map((supplier) => ({
    id: supplier.id,
    logoUrl: supplier.Business.logoUrl ?? "",
  }));

  return (
    <Dialog active={open} onDismiss={onDismiss}>
      <DialogContent className="m-0 flex flex-col gap-4 p-4">
        <div className="flex h-63.5 items-center justify-center gap-2 rounded-md bg-zinc-100">
          <div className="flex -space-x-4">
            {suppliers.map((supplier) => (
              <ProfilePicture
                key={supplier.id}
                size={isMobile ? "large" : "xl"}
                logoUrl={supplier.logoUrl}
                fallback="supplier-badge"
                className="relative"
              />
            ))}
          </div>
          <CustomRightArrowIcon className="w-12 text-[#8C23C7] sm:w-24" />
          <div className="flex h-18 min-h-18 w-18 min-w-18 flex-col items-center justify-center rounded-full bg-[#E8D3F4]/20 sm:h-25 sm:min-h-25 sm:w-25 sm:min-w-25">
            <div className="flex h-13 min-h-13 w-13 min-w-13 items-center justify-center rounded-full bg-[#E8D3F4] sm:h-19 sm:min-h-19 sm:w-19 sm:min-w-19">
              <Icon
                icon="SELLAR_DISTRIBUTION_TRUCK"
                viewBox="0 0 16 16"
                className="text-[#8C23C7]"
                size={isMobile ? 1 : 2}
              />
            </div>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 min-w-12 items-center justify-center rounded-lg bg-blue-50">
            <Icon icon="INFORMATION" size={1} className="text-blue-700" />
          </div>
          <div className="text-zinc-500">
            <H4 className="text-lg text-zinc-800">
              Did you know you can add more orders to your delivery?
            </H4>
            <p>
              With orders that are delivered by Sellar, you get free expert dray
              delivery to your chosen premises at a time that suits you, with
              orders delivered together where possible.
            </p>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className="flex flex-col justify-normal gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <label
          htmlFor="disable-distribution-dialog"
          className="flex cursor-pointer gap-2 text-zinc-800"
        >
          <InputCheckbox
            id="disable-distribution-dialog"
            checked={!canShowDialogAgain}
            onChange={(event) => {
              setCanShowDialogAgain(!event.target.checked);
            }}
          />
          <span>Don't show this again</span>
        </label>
        <div className="flex w-full gap-2 sm:w-fit">
          <Button
            className="w-full sm:w-fit"
            color="transparent"
            onClick={onDismiss}
          >
            Dismiss
          </Button>
          <Button className="w-full sm:w-fit" onClick={onAddMoreOrders}>
            Add more orders
          </Button>
        </div>
      </DialogCTA>
    </Dialog>
  );
};

type SellarCreditPitchProps = {
  creditCheckReveal?: ReactNode;
};
const SellarCreditPitch = ({ creditCheckReveal }: SellarCreditPitchProps) => {
  return (
    <div className="flex flex-col gap-6 rounded-2xl border border-zinc-200 bg-white p-6 shadow-md">
      <SellarCreditPitchTitle />
      {creditCheckReveal && creditCheckReveal}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <SellarCreditPitchFeature
          title="Boost cash flow"
          imageUrl="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-calendar.png"
          description="Get up to 60 days credit with EOM + 30 days credit terms with all
          participating suppliers"
        />
        <SellarCreditPitchFeature
          title="Save time"
          imageUrl="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-invoice.png"
          description="All Sellar Credit orders get wrapped into one monthly payment"
        />
        <SellarCreditPitchFeature
          title="Support the industry"
          imageUrl="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-money.png"
          description="Built for the drinks trade - zero fees or interest whilst your suppliers are paid on dispatch"
        />
      </div>
      <SellarCreditPitchActionPanel
        hasCreditCheckReveal={Boolean(creditCheckReveal)}
      />
    </div>
  );
};

const CustomRightArrowIcon = ({ ...props }: ComponentProps<"svg">) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 85 16"
      {...props}
      fill="currentColor"
    >
      <path d="M84.707 8.07a1 1 0 0 0 0-1.414L78.343.292a1 1 0 1 0-1.414 1.414l5.657 5.657-5.657 5.657a1 1 0 0 0 1.414 1.414l6.364-6.364ZM0 7.363v1h84v-2H0v1Z" />
    </svg>
  );
};

const SellarCreditPitchTitle = () => {
  const potentialNbDaysOfCredit = dayjs()
    .endOf("month")
    .add(30, "days")
    .diff(dayjs(), "days");

  const formattedPotentialNbDaysOfCredit = `${potentialNbDaysOfCredit} ${pluralize("day", potentialNbDaysOfCredit)}`;
  return (
    <div className="flex flex-col gap-1 text-zinc-700">
      <p className="text-xl font-semibold">Introducing Sellar Credit</p>
      <div className="flex items-center gap-1 text-sm">
        <Icon icon="INFORMATION_OUTLINE" className="h-3 w-3" />
        <p>
          With Sellar Credit you could have had{" "}
          <strong>{formattedPotentialNbDaysOfCredit}</strong> to pay for this
          order
        </p>
      </div>
    </div>
  );
};

type SellarCreditPitchCreditRevealProps = {
  creditLimitQuote: number | null;
  onClick: () => void;
};
const SellarCreditPitchCreditReveal = ({
  creditLimitQuote,
  onClick,
}: SellarCreditPitchCreditRevealProps) => {
  if (!creditLimitQuote) {
    return null;
  }

  const formattedCreditLimit = formatCurrency(creditLimitQuote);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full flex-col rounded-lg bg-linear-to-br from-[#B557F1] to-[#D213A6] p-px shadow-md"
    >
      <div className="flex w-full flex-col justify-between gap-4 rounded-lg border bg-purple-50 p-4 lg:flex-row">
        <div className="flex w-full items-center gap-4 lg:w-2/3">
          <Icon
            icon="BELL_ALERT"
            className="min-w-6 text-[#C63FD0]"
            size={1.2}
          />
          <div className="text-start text-zinc-800">
            <p className="font-semibold">Unlock your credit account</p>
            <p className="font-normal">
              Your company is eligible for a {formattedCreditLimit} credit limit
            </p>
          </div>
        </div>
        <div className="flex w-full flex-1 items-center gap-4">
          <div className="flex w-full flex-1 items-center justify-center gap-2 rounded-lg border border-dashed border-purple-400/60 px-14 py-4 text-zinc-800">
            <Icon icon="LOCK" className="text-zinc-800" />
            {formattedCreditLimit}
          </div>

          <Icon
            icon="CHEVRON_RIGHT"
            size={1.2}
            className="hidden text-black lg:flex"
          />
        </div>
      </div>
    </button>
  );
};

type SellarCreditPitchFeatureProps = {
  imageUrl: string;
  title: string;
  description: string;
};
const SellarCreditPitchFeature = ({
  imageUrl,
  title,
  description,
}: SellarCreditPitchFeatureProps) => {
  const { isDesktop } = useIsDesktop();

  if (!isDesktop) {
    return (
      <div className="flex gap-6 rounded-lg border border-zinc-200 p-4">
        <img src={imageUrl} className="h-28 w-28" />
        <div className="flex flex-col gap-2">
          <span className="text-lg font-semibold">{title}</span>
          <p className="text-sm font-normal">{description}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center rounded-2xl border border-zinc-200 bg-zinc-100">
      <div className="flex w-full items-center justify-center rounded-t-2xl bg-white p-4">
        <img src={imageUrl} className="h-28 w-28" />
      </div>
      <div className="flex flex-col gap-2 border-t border-zinc-200 p-4 text-zinc-800">
        <span className="text-lg font-semibold">{title}</span>
        <p className="text-sm font-normal">{description}</p>
      </div>
    </div>
  );
};

const SellarCreditPitchActionPanel = ({
  hasCreditCheckReveal,
}: {
  hasCreditCheckReveal: boolean;
}) => {
  const { trackFeedContent } = useTrackEndOfCheckoutFeed();

  const navigate = useNavigate();
  const callToAction = hasCreditCheckReveal
    ? "Claim your credit"
    : "Check your credit limit";
  return (
    <div className="flex w-full flex-col gap-4 lg:flex-row">
      <Button
        onClick={() => {
          trackFeedContent({
            title: "Sellar Credit Pitch",
          });
          window.open(
            "https://sellar.io/sellar-credit-for-buyers?utm_source=sellar-app&utm_medium=order-success",
            "_blank",
          );
        }}
        color="secondary"
        className="w-full flex-1"
      >
        Find out more <Icon icon="ARROW_TOP_RIGHT" />
      </Button>
      <Button
        onClick={() => {
          trackFeedContent({
            title: "Sellar Credit Pitch",
          });
          navigate(`${DISCOVER}?showSellarCreditOnboarding=true`);
        }}
        color="primary"
        className="w-full flex-1"
      >
        {callToAction}
      </Button>
    </div>
  );
};

type EndOfCheckoutHorizontalListProps<T> = {
  title: string;
  subtitle: string;
  items: T[];
  loading: boolean;
  renderItem: (item: T) => ReactNode;
};

const EndOfCheckoutHorizontalList = <T,>({
  title,
  subtitle,
  items,
  loading,
  renderItem,
}: EndOfCheckoutHorizontalListProps<T>) => {
  return (
    <div className="flex flex-col">
      <div className="text-lg font-semibold text-zinc-800">{title}</div>
      <p className="text-base text-zinc-600">{subtitle}</p>
      {loading ? (
        <SpinnerLegacy />
      ) : (
        <DiscoveryHorizontalList containerClassName="px-0 pt-4" hideViewMore>
          {items.map((item) => renderItem(item))}
        </DiscoveryHorizontalList>
      )}
    </div>
  );
};

type EndOfCheckoutProduct = DiscoveryListProductCardProps & {
  Parent?: { id: number };
};
type EndOfCheckoutProductCardProps = {
  product: EndOfCheckoutProduct;
  onProductClick: (product: EndOfCheckoutProduct) => void;
};

const EndOfCheckoutProductCard = ({
  product,
  onProductClick,
}: EndOfCheckoutProductCardProps) => {
  return (
    <div className={twMerge("w-50 min-w-50 flex-none")}>
      <DiscoveryListProductCard
        product={product}
        onClick={() => onProductClick(product)}
      />
    </div>
  );
};

type EndOfCheckoutSupplierCardProps = {
  supplier: SupplierRecentlyAddedNewListResponseType[number];
  onSupplierClick: (params: { supplierUrlId: string }) => void;
};
const EndOfCheckoutSupplierCard = ({
  supplier,
  onSupplierClick,
}: EndOfCheckoutSupplierCardProps) => {
  return (
    <div className={twMerge("w-50 min-w-50 flex-none")}>
      <DiscoverySupplierCardCompact
        supplier={{ ...supplier, StorefrontAvailability: null }}
        onClick={() => onSupplierClick({ supplierUrlId: supplier.urlId })}
        footer={
          <div className="w-full px-2">
            <SupplierAddToListButton supplierId={supplier.id} />
          </div>
        }
        className={twMerge("w-50 flex-none shadow-md")}
      />
    </div>
  );
};

const SupplierAddToListButton = ({ supplierId }: { supplierId: number }) => {
  const { isAdded, handleAddToList } = useSupplierAddToList({
    supplierId,
  });
  return <AddToListButton onClick={handleAddToList} isAdded={isAdded} />;
};

const useEndOfCheckoutProducts = (productIds: number[]) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  const query = useQuery({
    queryKey: [business?.id, ServerStateKeys.Products, productIds],
    queryFn: () => {
      const requests = productIds.map((productId) =>
        apiV2Request({
          user,
          business,
          method: "get",
          path: `products/${productId}/buyer`,
          retailerId: business?.Retailer?.id,
        }).then(
          (data) => apiV2AssertResponse<BuyerProductGetResponseType>(data).data,
        ),
      );

      return Promise.all(requests);
    },
    ...getDefaultQueryOptions<BuyerProductGetResponseType[]>({
      enabled: productIds.length > 0 && !!business?.id,
    }),
  });

  return {
    loading: query.isPending,
    error: query.error,
    products: query.data ?? [],
  };
};

type EndOfCheckoutRecentlyViewedProductsProps = {
  recentlyViewed: DiscoveryV2RecentlyViewedStoreState["recentlyViewed"];
  onProductClick: (params: HandleOnClickProductParams) => void;
};
const EndOfCheckoutRecentlyViewedProducts = ({
  recentlyViewed,
  onProductClick,
}: EndOfCheckoutRecentlyViewedProductsProps) => {
  const recentlyViewedProductIds = recentlyViewed.map((item) => item.id);
  const { loading, error, products } = useEndOfCheckoutProducts(
    recentlyViewedProductIds,
  );

  if (error || products.length === 0) {
    return null;
  }

  const endOfCheckoutProducts = mapProductsToEndOfCheckoutProducts(products);
  const title = "Pick up where you left off";

  return (
    <EndOfCheckoutHorizontalList
      title={title}
      subtitle="Browse through your recently viewed products"
      items={endOfCheckoutProducts}
      loading={loading}
      renderItem={(product) => {
        return (
          <EndOfCheckoutProductCard
            key={product.id}
            product={product}
            onProductClick={(product) => {
              onProductClick({
                supplierUrlId: product.Supplier?.urlId ?? "",
                productId: product.id,
                contentTitle: title,
              });
            }}
          />
        );
      }}
    />
  );
};

const EndOfCheckoutRecentlyAddedByMySupplierProducts = ({
  onProductClick,
}: {
  onProductClick: (params: HandleOnClickProductParams) => void;
}) => {
  const recentlyAddedProductsQuery = useDiscoveryV2ProductList({
    limit: PRODUCTS_PER_SLIDER,
    sortBy: "updatedAt",
    query: [
      {
        type: "mySuppliersOnly",
        modifier: "EQUALS",
        value: true,
      },
      {
        type: "inStockItemsOnly",
        modifier: "EQUALS",
        value: true,
      },
    ],
  });

  const recentlyAddedProductParentIds = [
    ...new Set(
      recentlyAddedProductsQuery.data?.pages[0]
        ?.map((product) => product.Parent?.id)
        .filter(Boolean) ?? [],
    ),
  ];

  const { loading, error, products } = useEndOfCheckoutProducts(
    recentlyAddedProductParentIds,
  );

  if (error || products.length === 0) {
    return null;
  }

  const endOfCheckoutProducts = mapProductsToEndOfCheckoutProducts(products);
  const title = "Fresh drops from your suppliers";

  return (
    <EndOfCheckoutHorizontalList
      title={title}
      subtitle="Don't miss out on the freshest and most exciting products from suppliers in your list"
      items={endOfCheckoutProducts}
      loading={loading}
      renderItem={(product) => {
        return (
          <EndOfCheckoutProductCard
            key={product.id}
            product={product}
            onProductClick={(product) => {
              onProductClick({
                supplierUrlId: product.Supplier?.urlId ?? "",
                productId: product.id,
                contentTitle: title,
              });
            }}
          />
        );
      }}
    />
  );
};

const EndOfCheckoutRecentlyAddedProducts = ({
  onProductClick,
}: {
  onProductClick: (params: HandleOnClickProductParams) => void;
}) => {
  const recentlyAddedProductsQuery = useDiscoveryV2ProductList({
    limit: PRODUCTS_PER_SLIDER,
    sortBy: "updatedAt",
    query: [
      {
        type: "inStockItemsOnly",
        modifier: "EQUALS",
        value: true,
      },
    ],
  });

  const recentlyAddedProductParentIds = [
    ...new Set(
      recentlyAddedProductsQuery.data?.pages[0]
        ?.map((product) => product.Parent?.id)
        .filter(Boolean) ?? [],
    ),
  ];

  const { loading, error, products } = useEndOfCheckoutProducts(
    recentlyAddedProductParentIds,
  );

  if (error || products.length === 0) {
    return null;
  }

  const endOfCheckoutProducts = mapProductsToEndOfCheckoutProducts(products);
  const title = "New releases on the Market";

  return (
    <EndOfCheckoutHorizontalList
      title={title}
      subtitle="Don't miss out on the freshest and most exciting products from suppliers across the Market"
      items={endOfCheckoutProducts}
      loading={loading}
      renderItem={(product) => {
        return (
          <EndOfCheckoutProductCard
            key={product.id}
            product={product}
            onProductClick={(product) => {
              onProductClick({
                supplierUrlId: product.Supplier?.urlId ?? "",
                productId: product.id,
                contentTitle: title,
              });
            }}
          />
        );
      }}
    />
  );
};

const EndOfCheckoutDirectDeliverySuppliers = ({
  onSupplierClick,
}: {
  onSupplierClick: (params: HandleOnClickSupplierParams) => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  const directSuppliersQuery = useSupplierListByDistance({
    offset: 0,
    limit: SUPPLIERS_PER_SLIDER,
    lng: business?.Location?.lng ?? defaultLondonLocation.lng,
    lat: business?.Location?.lat ?? defaultLondonLocation.lat,
    distance: 200,
    orderBy: "distance",
    directDelivery: true,
  });
  const postcode =
    business?.DeliveryAddress.postalCode.split(" ")[0] ?? undefined;

  const directSuppliers =
    directSuppliersQuery.data?.pages.flatMap((page) => page) ?? [];

  if (directSuppliers.length === 0) {
    return null;
  }

  const loading = directSuppliersQuery.isPending;
  const title = "Discover suppliers delivering direct";

  return (
    <EndOfCheckoutHorizontalList
      title={`${title} to ${postcode}`}
      subtitle="Don't miss out on the freshest and most exciting products from suppliers across the Market"
      items={directSuppliers}
      loading={loading}
      renderItem={(supplier) => {
        return (
          <EndOfCheckoutSupplierCard
            supplier={supplier}
            onSupplierClick={({ supplierUrlId }) =>
              onSupplierClick({
                supplierUrlId,
                contentTitle: title,
                supplierName: supplier.Business.name,
              })
            }
          />
        );
      }}
    />
  );
};

const EndOfCheckoutRecommendedDirectDeliveryProducts = ({
  onProductClick,
}: {
  onProductClick: (params: HandleOnClickProductParams) => void;
}) => {
  const recommendedProductsQuery = useDiscoveryV2ProductList({
    limit: PRODUCTS_PER_SLIDER,
    sortBy: "popular",
    query: [
      {
        type: "inStockItemsOnly",
        modifier: "EQUALS",
        value: true,
      },
      {
        type: "directDelivery",
        modifier: "EQUALS",
        value: true,
      },
    ],
  });

  const recommendedProductIds = [
    ...new Set(
      recommendedProductsQuery.data?.pages[0]
        ?.map((product) => product.Parent?.id)
        .filter(Boolean) ?? [],
    ),
  ];

  const { loading, error, products } = useEndOfCheckoutProducts(
    recommendedProductIds,
  );

  if (error || products.length === 0) {
    return null;
  }

  const endOfCheckoutProducts = mapProductsToEndOfCheckoutProducts(products);
  const title = "Recommended products from suppliers near you";

  return (
    <EndOfCheckoutHorizontalList
      title={title}
      subtitle="Order from the largest range of the UK's best craft, direct from the producers"
      items={endOfCheckoutProducts}
      loading={loading}
      renderItem={(product) => {
        return (
          <EndOfCheckoutProductCard
            key={product.id}
            product={product}
            onProductClick={(product) => {
              onProductClick({
                supplierUrlId: product.Supplier?.urlId ?? "",
                productId: product.id,
                contentTitle: title,
              });
            }}
          />
        );
      }}
    />
  );
};

const EndOfCheckoutPreorderProducts = ({
  onProductClick,
}: {
  onProductClick: (params: HandleOnClickProductParams) => void;
}) => {
  const preOrderQuery = useDiscoveryPreOrdersProducts({
    limit: PRODUCTS_PER_SLIDER,
  });
  const preOrders = preOrderQuery.data?.pages.flatMap((page) => page) ?? [];

  if (preOrderQuery.error || preOrders.length === 0) {
    return null;
  }
  const loading = preOrderQuery.isPending;
  const title = "Pre-orders on the Market";

  return (
    <EndOfCheckoutHorizontalList
      title={title}
      subtitle="Get ahead of the crowd and secure the freshest brews while stock lasts"
      items={preOrders}
      loading={loading}
      renderItem={(product) => {
        return (
          <EndOfCheckoutProductCard
            product={product}
            key={product.id}
            onProductClick={(product) => {
              onProductClick({
                supplierUrlId: product.Supplier?.urlId ?? "",
                productId: product.id,
                contentTitle: title,
              });
            }}
          />
        );
      }}
    />
  );
};

const mapDiscoveryV2ProductToEndOfCheckoutProduct = (
  product: DiscoveryV2ListResponseType[number],
): EndOfCheckoutProduct => {
  const containerTypes: string[] = [];
  return {
    id: product.id,
    name: product.name,
    imageUrl: product.Parent?.imageUrl ?? null,
    heroImageUrl: product.Parent?.heroImageUrl ?? null,
    abv: product.Parent?.abv ?? null,
    kind: product.Parent?.kind ?? null,
    vegan: product.Parent?.vegan ?? false,
    lactoseFree: product.Parent?.lactoseFree ?? false,
    glutenFree: product.Parent?.glutenFree ?? false,
    containerTypes,
    ProductImages: [],
    Supplier: {
      id: product.Supplier.id,
      imageUrl: product.Supplier.imageUrl,
      urlId: product.Supplier.urlId,
      Business: product.Supplier.Business
        ? {
            id: product.Supplier.id,
            name: product.Supplier.Business.name,
            logoUrl: product.Supplier.Business.logoUrl,
          }
        : null,
    },
    Parent: product.Parent
      ? {
          id: product.Parent.id,
        }
      : undefined,
  };
};

const EndOfCheckoutSpiritsProducts = ({
  onProductClick,
}: {
  onProductClick: (params: HandleOnClickProductParams) => void;
}) => {
  const { products, isPending } = useDiscoverySpiritsProducts({
    limit: 15,
  });

  if (products.length === 0) {
    return null;
  }

  const title = "Spirit products to add to your delivery";
  const endOfCheckoutProducts = products.map(
    mapDiscoveryV2ProductToEndOfCheckoutProduct,
  );

  return (
    <EndOfCheckoutHorizontalList
      title={title}
      subtitle="Go beyond beer and add any of the spirit products listed below to your delivery"
      items={endOfCheckoutProducts}
      loading={isPending}
      renderItem={(product) => {
        return (
          <EndOfCheckoutProductCard
            key={product.id}
            product={product}
            onProductClick={(product) => {
              onProductClick({
                supplierUrlId: product.Supplier?.urlId ?? "",
                productId: product.Parent?.id ?? product.id,
                contentTitle: title,
              });
            }}
          />
        );
      }}
    />
  );
};

const EndOfCheckoutBestPricePromise = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-purple-300 bg-purple-50 p-8 shadow-md md:flex-row">
      <img
        className="h-16 w-16"
        src="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-checkmark.png"
      />
      <p className="text-center font-semibold text-zinc-600 md:text-left">
        On Sellar you're ordering direct with suppliers - your best price is
        guaranteed with our{" "}
        <button
          type="button"
          onClick={onClick}
          className="inline-flex gap-1 underline hover:cursor-pointer"
        >
          Best Price Promise
          <Icon icon="ARROW_TOP_RIGHT" />
        </button>
      </p>
    </div>
  );
};

const EndOfCheckoutSocials = ({
  onClickSocial,
}: {
  onClickSocial: (params: { type: string; url: string }) => void;
}) => {
  return (
    <div className="flex items-center justify-center rounded-lg border border-zinc-300 p-8 text-xl font-semibold text-zinc-600 shadow-md">
      <div className="flex flex-col items-center gap-4 md:flex-row">
        <p>Follow us on our socials</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              onClickSocial({
                type: "instagram",
                url: "https://instagram.com/sellar.io",
              });
            }}
          >
            <img
              className="h-12 w-12"
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-instagram-logo.png"
              alt="instagram-logo"
            />
          </button>
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              onClickSocial({
                type: "linkedin",
                url: "https://www.linkedin.com/company/sellar-io",
              });
            }}
          >
            <img
              className="h-12 w-12"
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-linkedin-logo.png"
              alt="linkedin-logo"
            />
          </button>

          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              onClickSocial({
                type: "spotify",
                url: "https://open.spotify.com/show/3mhXTAzySdIuuFxpoqunbW?si=8bc2b43901944405",
              });
            }}
          >
            <img
              className="h-12 w-12"
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-spotify-logo.png"
              alt="spotify-logo"
            />
          </button>
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              onClickSocial({
                type: "trademailer",
                url: "https://sellar.io/trade-mailer",
              });
            }}
          >
            <img
              className="h-12 w-12"
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/end-of-checkout-mail-logo.png"
              alt="mail-logo"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

const mapProductsToEndOfCheckoutProducts = (
  products: BuyerProductGetResponseType[],
): EndOfCheckoutProduct[] => {
  const mappedProducts = products.map((product) => {
    const containerTypes: string[] = [];
    const types = product.Products.map((product) => product.containerType);
    if (types.includes("keg")) containerTypes.push("Keg");
    if (types.includes("bottle")) containerTypes.push("Bottle");
    if (types.includes("cask")) containerTypes.push("Cask");
    if (types.includes("can")) containerTypes.push("Can");
    if (types.includes("bagInBox")) containerTypes.push("Bag In Box");
    return {
      ...product,
      containerTypes: [...new Set(containerTypes)],
      Parent: undefined,
    };
  });
  return mappedProducts;
};

type EndOfCheckoutFeedItem =
  | "sellar-distribution"
  | "sellar-credit-pitch-with-credit-reveal"
  | "sellar-credit-pitch-without-credit-reveal"
  | "recently-viewed"
  | "recently-added-by-my-suppliers"
  | "recently-added"
  | "suppliers-delivering-direct"
  | "best-price-promise"
  | "feedback-survey"
  | "recommended-from-suppliers-near-you"
  | "pre-orders"
  | "beyond-beer"
  | "best-in-beer"
  | "link-to-socials"
  | "spirits-section";

type useEndOfCheckoutFeedProps = {
  recentlyViewed: DiscoveryV2RecentlyViewedStoreState["recentlyViewed"];
  fulfillmentType: OrderGetResponseType["fulfillmentType"] | undefined;
};

type EndOfCheckoutUserType =
  | "first-time-user"
  | "casual-user"
  | "frequent-user";

type FeedKey = "withBrowsingHistory" | "withoutBrowsingHistory";

const baseFeed: Record<
  EndOfCheckoutUserType,
  Record<FeedKey, Array<EndOfCheckoutFeedItem>>
> = {
  "first-time-user": {
    withBrowsingHistory: [
      "suppliers-delivering-direct",
      "best-price-promise",
      "recommended-from-suppliers-near-you",
      "link-to-socials",
    ],
    withoutBrowsingHistory: [
      "suppliers-delivering-direct",
      "best-price-promise",
      "recommended-from-suppliers-near-you",
      "link-to-socials",
    ],
  },
  "casual-user": {
    withBrowsingHistory: [
      "recently-viewed",
      "recently-added",
      "suppliers-delivering-direct",
      "best-price-promise",
      "link-to-socials",
    ],
    withoutBrowsingHistory: [
      "recently-added",
      "suppliers-delivering-direct",
      "best-price-promise",
      "link-to-socials",
    ],
  },
  "frequent-user": {
    withBrowsingHistory: [
      "recently-viewed",
      "recently-added-by-my-suppliers",
      "feedback-survey",
      "pre-orders",
      "link-to-socials",
    ],
    withoutBrowsingHistory: [
      "recently-viewed",
      "recently-added-by-my-suppliers",
      "feedback-survey",
      "pre-orders",
      "link-to-socials",
    ], // Unlikely to happen
  },
};

const sellarCreditPitches: Record<
  CompanyCreditInfoResponseType["status"],
  EndOfCheckoutFeedItem | undefined
> = {
  approved: undefined,
  creditCheckNotPerformed: "sellar-credit-pitch-without-credit-reveal",
  declined: undefined,
  eligible: "sellar-credit-pitch-with-credit-reveal",
  noCreditOffered: undefined,
};

const useEndOfCheckoutFeed = ({
  recentlyViewed,
  fulfillmentType,
}: useEndOfCheckoutFeedProps) => {
  const { business } = useSellarCurrentBusiness();
  const retailerId = business?.Retailer?.id ?? 0;
  const buyerRelationshipCount = useBuyerRelationshipCount({
    query: [],
  });
  const sellarCreditBalanceQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: retailerId,
    },
    {
      enabled: !!retailerId,
    },
  );

  const nbBuyerRelationships = buyerRelationshipCount.data ?? 0;

  const userType: EndOfCheckoutUserType =
    nbBuyerRelationships === 1
      ? "first-time-user"
      : nbBuyerRelationships <= 10
        ? "casual-user"
        : "frequent-user";

  const feedKey: FeedKey =
    recentlyViewed.length > 0
      ? "withBrowsingHistory"
      : "withoutBrowsingHistory";

  let feed = new Set(baseFeed[userType][feedKey]);

  const sellarCreditPitch =
    sellarCreditPitches[
      sellarCreditBalanceQuery.data?.status ?? "creditCheckNotPerformed"
    ];

  const isOrderSellarDistribution = fulfillmentType === "sellarDistribution";

  if (sellarCreditPitch && !isOrderSellarDistribution) {
    feed = new Set([sellarCreditPitch, ...feed]);
  }

  // When the order is SD, we want to override the
  // entire feed and make it SD focused
  if (isOrderSellarDistribution) {
    feed = new Set(["sellar-distribution", "spirits-section"]);
  }

  return {
    feed,
    creditLimitQuote: sellarCreditBalanceQuery.data?.creditLimitQuote ?? null,
  };
};

type OrderAndSupplierCardProps = Pick<
  CheckoutSuccessSummaryProps,
  | "supplierLogoUrl"
  | "supplierName"
  | "orderItems"
  | "orderTotal"
  | "orderVatPercentage"
> & { paymentAmount: number };

export const OrderAndSupplierCard = ({
  supplierLogoUrl,
  supplierName,
  orderItems,
  orderTotal,
  orderVatPercentage,
}: OrderAndSupplierCardProps) => {
  const nbOrderItems = orderItems.length;
  const vatMessage =
    Number(orderVatPercentage) > 0 ? "Total (inc. VAT)" : "Total (No VAT)";

  return (
    <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm">
      <div className="flex gap-4">
        <ImageWithFallback
          img={supplierLogoUrl}
          className="h-10 w-10 rounded-full"
          fallback="supplier-badge"
        />
        <div className="flex flex-col">
          <span className="font-semibold text-zinc-800">{supplierName}</span>
          <span className="text-zinc-600">
            {`${nbOrderItems} ${pluralize("item", nbOrderItems)}`}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col">
          <span className="text-zinc-800">
            <span className="font-semibold">{formatCurrency(orderTotal)}</span>
          </span>
          <span className="text-zinc-600">{vatMessage}</span>
        </div>
      </div>
    </div>
  );
};

type SuccessStatusCardPaymentWithDepositProps = {
  orderStatus: string;
  paymentMethod: string;
  amountPaid: number;
};
const SuccessStatusCardPaymentWithDeposit = ({
  orderStatus,
  paymentMethod,
  amountPaid,
}: SuccessStatusCardPaymentWithDepositProps) => {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-emerald-300 bg-emerald-100 p-3">
      <div className="flex items-center justify-between text-sm text-emerald-800">
        <span>Order status</span>
        <span className="font-bold">{orderStatus}</span>
      </div>
      <div className="flex items-center justify-between text-sm text-emerald-800">
        <span>Payment method</span>
        <span className="font-bold">{paymentMethod}</span>
      </div>
      <div className="flex items-center justify-between text-sm text-emerald-800">
        <span>Amount paid</span>
        <span className="font-bold">{formatCurrency(amountPaid)}</span>
      </div>
    </div>
  );
};

type StatusUpdateProps = {
  title: string;
  description?: string;
};
const SuccessStatusUpdate = ({ title }: StatusUpdateProps) => {
  return (
    <div className="flex items-center justify-between rounded-lg border border-emerald-300 bg-emerald-100 p-3">
      <span className="text-sm text-emerald-800">{title}</span>
      <Icon icon="CHECK_CIRCLE" className="text-emerald-800" />
    </div>
  );
};

const PendingStatusUpdate = ({ title, description }: StatusUpdateProps) => {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-zinc-300 bg-zinc-100 p-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-zinc-800">{title}</span>
        <Icon icon="TIMER_SAND_EMPTY" className="animate-spin text-zinc-800" />
      </div>
      {description && <p>{description}</p>}
    </div>
  );
};

const FailedStatusUpdate = ({
  title,
  description,
  action,
}: StatusUpdateProps & { action: ReactNode }) => {
  return (
    <>
      <div className="flex flex-col gap-2 rounded-lg border border-red-300 bg-red-100 p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-red-800">{title}</span>
          <Icon icon="ALERT" className="text-red-800" />
        </div>
        {description && <p className="text-red-800">{description}</p>}
        {action}
      </div>
    </>
  );
};

export const NonAsyncPaymentStatusUpdate = ({
  paymentType,
  orderStatus,
  hasPaymentLink,
  paymentMethod,
}: Pick<
  CheckoutSuccessSummaryProps,
  "orderStatus" | "paymentType" | "hasPaymentLink"
> & {
  paymentMethod?: string;
}) => {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-emerald-300 bg-emerald-100 p-3 text-sm text-emerald-800">
      <div className="flex justify-between">
        <span>Order status</span>
        <span className="font-semibold">{orderStatus}</span>
      </div>
      <div className="flex justify-between">
        <span>Payment method</span>
        <span className="font-semibold">
          {paymentMethod ?? mapToPaymentMethod(paymentType, hasPaymentLink)}
        </span>
      </div>
    </div>
  );
};

type CheckoutSuccessSummaryProps = {
  supplierName: string;
  supplierLogoUrl: string;
  supplierId: number | undefined;
  orderId: number;
  orderTotal: number;
  orderStatus: OrderStage;
  paymentType: OrderPaymentType | undefined;
  paymentStatus: CheckoutPaymentStatusResponseType["status"];
  orderItems: Array<{ id: number }>;
  orderVatPercentage: number;
  stripeAccountId: string;
  paymentIntentId: string;
  retailerBusinessId: number | undefined;
  twoOrder: ReturnType<typeof useCheckoutSuccessPageData>["twoOrder"];
  onClickBackToMarket: () => void;
  onClickReviewOrEditOrder: (orderId: number) => void;
  hasPaymentLink: boolean;
};

const asyncPaymentTypes: OrderPaymentType[] = [
  "cardPaymentWithDeposit",
  "checkout",
  "sellarCredit",
  "cardPayment",
];

export const CheckoutSuccessSummary = ({
  supplierName,
  supplierLogoUrl,
  supplierId,
  orderId,
  paymentType,
  orderStatus,
  orderTotal,
  orderItems,
  paymentStatus,
  orderVatPercentage,
  stripeAccountId,
  paymentIntentId,
  retailerBusinessId,
  twoOrder,
  onClickBackToMarket,
  onClickReviewOrEditOrder,
  hasPaymentLink,
}: CheckoutSuccessSummaryProps) => {
  const { business } = useSellarCurrentBusiness();
  const { changeBusiness } = useSellarUser();
  const navigate = useNavigate();
  const { isActive } = useRemoteFeatureFlags();
  const inAppNotificationActive = isActive("inAppNotifications");
  const { isDesktop } = useIsDesktop();
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const shouldShowMoreInfo = isDesktop ? true : showMoreInfo;

  const [noticationPreferencesActive, setNoticationPreferencesActive] =
    useState(false);
  const isPaymentStatusDelayed = useIsPaymentStatusDelayed({ paymentStatus });

  const [notificationPromptShownAt, setNotificationPromptShownAt] =
    useLocalStorage<Date | null>("notificationPromptShownAt", null);

  useEffect(() => {
    const monthsSinceShown = dayjs().diff(notificationPromptShownAt, "months");
    if (notificationPromptShownAt === null || monthsSinceShown >= 6) {
      const timeoutId = window.setTimeout(() => {
        setNotificationPromptShownAt(dayjs().toDate());
        setNoticationPreferencesActive(true);
      }, 2000);

      return () => window.clearTimeout(timeoutId);
    }
  }, [notificationPromptShownAt, setNotificationPromptShownAt]);

  const isAsyncPaymentType = asyncPaymentTypes.includes(paymentType ?? "");

  const cardPaymentFailed =
    paymentStatus === "failed" && paymentType === "checkout";

  const cardPaymentWithDepositFailed =
    paymentStatus === "failed" && paymentType === "cardPaymentWithDeposit";

  const sellarCreditPaymentFailed =
    paymentType === "sellarCredit" &&
    (paymentStatus === "failed" || paymentStatus === "not_started");

  const isPaymentPending = paymentStatus === "pending" && isAsyncPaymentType;

  const paymentAmount =
    paymentType === "cardPaymentWithDeposit"
      ? calculateCardPaymentDeposit(orderTotal)
      : orderTotal;

  const isTwoOrder = Boolean(twoOrder);

  return (
    <>
      <div className="flex h-full flex-1 flex-col gap-2 bg-gray-100 px-4 py-8 lg:py-16">
        <div className="relative flex w-fit self-center lg:self-start">
          <ImageWithFallback
            img={supplierLogoUrl}
            className="h-12 w-12 rounded-full"
            fallback="supplier-badge"
          />
          <Icon
            icon="CHECKBOX_MARKED_CIRCLE"
            className="absolute -right-1 -bottom-1 h-2 w-2 text-emerald-600"
          />
        </div>
        <p className="text-center text-lg font-semibold lg:text-left">
          Thanks for ordering with {supplierName}
        </p>
        {shouldShowMoreInfo && (
          <>
            <p className="text-center lg:text-left">
              Your order <strong>#{orderId}</strong> has been placed
              successfully. We'll send you an email when {supplierName} accepts
              your order shortly.
            </p>
            {!inAppNotificationActive && (
              <Button
                color="transparent"
                className="self-center lg:self-start"
                onClick={() => {
                  if (
                    retailerBusinessId &&
                    retailerBusinessId !== business?.id
                  ) {
                    changeBusiness(retailerBusinessId);
                  }
                  navigate(`${ORDERS}/${orderId}?tab=All+orders`);
                }}
              >
                <span>Review or Edit Order</span>
                <Icon icon="CHEVRON_RIGHT" />
              </Button>
            )}
          </>
        )}

        {supplierId && <BuyerFavouriteSection supplierId={supplierId} />}

        <hr className="border-t border-zinc-200" />
        <OrderAndSupplierCard
          supplierLogoUrl={supplierLogoUrl}
          supplierName={supplierName}
          orderTotal={orderTotal}
          paymentAmount={paymentAmount}
          orderItems={orderItems}
          orderVatPercentage={orderVatPercentage}
        />
        {!isAsyncPaymentType && (
          <NonAsyncPaymentStatusUpdate
            paymentType={paymentType}
            orderStatus={orderStatus}
            hasPaymentLink={hasPaymentLink}
          />
        )}
        {isAsyncPaymentType && paymentType !== "cardPaymentWithDeposit" && (
          <SuccessStatusUpdate title="Order placed" />
        )}
        {isPaymentPending && !isPaymentStatusDelayed && (
          <PendingStatusUpdate title="Payment pending" />
        )}
        {isPaymentPending && isPaymentStatusDelayed && (
          <PendingStatusUpdate
            title="Payment pending"
            description={`We're still waiting on ${mapToPaymentProvider({ paymentType, isTwoOrder })} to process your payment. You can continue to browse Sellar and check back later.`}
          />
        )}
        {paymentStatus === "succeeded" &&
          paymentType !== "cardPaymentWithDeposit" && (
            <SuccessStatusUpdate title="Payment sent" />
          )}
        {cardPaymentFailed && (
          <FailedStatusUpdate
            title="Payment failed"
            description={"Please retry your payment using the button below"}
            action={
              <RetryStripePaymentButton
                stripeAccountId={stripeAccountId}
                supplierName={supplierName}
                paymentAmount={paymentAmount}
                paymentIntentId={paymentIntentId}
              />
            }
          />
        )}

        {cardPaymentWithDepositFailed && (
          <FailedStatusUpdate
            title="Payment failed"
            description={"Please retry your payment using the button below"}
            action={
              <RetryCardPaymentWithDepositButton
                stripeAccountId={stripeAccountId}
                supplierName={supplierName}
                paymentAmount={paymentAmount}
                paymentIntentId={paymentIntentId}
              />
            }
          />
        )}
        {sellarCreditPaymentFailed && (
          <FailedStatusUpdate
            title="Payment failed"
            description="Please retry your payment using the button below"
            action={<RetrySellarCreditPaymentButton orderId={orderId} />}
          />
        )}

        {paymentStatus === "succeeded" &&
          paymentType === "cardPaymentWithDeposit" && (
            <SuccessStatusCardPaymentWithDeposit
              orderStatus={orderStatus}
              paymentMethod="Card payment with deposit"
              amountPaid={paymentAmount}
            />
          )}

        <div className="flex flex-1 flex-col items-center justify-end gap-2">
          <Button
            color="secondary"
            className="w-full"
            onClick={() => {
              onClickBackToMarket();
            }}
          >
            <span>Back to the Market</span>
          </Button>
          {shouldShowMoreInfo && inAppNotificationActive && (
            <>
              <Button
                color="transparent"
                onClick={() => {
                  if (
                    retailerBusinessId &&
                    retailerBusinessId !== business?.id
                  ) {
                    changeBusiness(retailerBusinessId);
                  }
                  onClickReviewOrEditOrder(orderId);
                }}
              >
                <span>Review or Edit Order</span>
              </Button>
            </>
          )}
        </div>
        {shouldShowMoreInfo && !isDesktop && (
          <Button
            color="transparent"
            className="w-full"
            onClick={() => setShowMoreInfo(false)}
          >
            <span>Less info</span>
            <Icon icon="CHEVRON_UP" />
          </Button>
        )}
        {!shouldShowMoreInfo && !isDesktop && (
          <Button
            color="transparent"
            className="w-full"
            onClick={() => setShowMoreInfo(true)}
          >
            <span>More info</span>
            <Icon icon="CHEVRON_DOWN" />
          </Button>
        )}
      </div>
      <NotificationSettingsPromptDialog
        active={noticationPreferencesActive}
        onDismiss={() => setNoticationPreferencesActive(false)}
      />
    </>
  );
};

const BuyerFavouriteSection = ({ supplierId }: { supplierId: number }) => {
  const buyerRelationshipListQuery = useBuyerRelationshipList({
    query: [
      {
        type: "supplierId",
        modifier: "EQUALS",
        value: supplierId,
      },
    ],
    limit: 1,
  });

  const buyerRelationship = buyerRelationshipListQuery.data?.pages[0]?.[0];
  return (
    <>
      {buyerRelationship?.id && (
        <AddToFavouritesGroup buyerRelationship={buyerRelationship} />
      )}
    </>
  );
};

const mapToPaymentProvider = ({
  paymentType,
  isTwoOrder,
}: {
  paymentType: OrderPaymentType | undefined;
  isTwoOrder: boolean;
}) => {
  switch (paymentType) {
    case "checkout":
      return "Stripe";
    case "sellarCredit":
      return isTwoOrder ? "Two" : "Hokodo";
    case "cardPayment":
      return "Ryft";
    default:
      return "Payment Provider";
  }
};

const DELAY_TIMEOUT = 60; //60 seconds

const useIsPaymentStatusDelayed = ({
  paymentStatus,
}: {
  paymentStatus: CheckoutPaymentStatusResponseType["status"];
}) => {
  const [isDelayed, setIsDelayed] = useState(false);
  const [previousPaymentStatus, setPreviousPaymentStatus] =
    useState(paymentStatus);
  const timerRef = useRef<number | null>(null);

  if (paymentStatus !== previousPaymentStatus) {
    setPreviousPaymentStatus(paymentStatus);
    if (paymentStatus !== "pending") setIsDelayed(false);
  }

  useEffect(() => {
    if (paymentStatus === "pending") {
      if (!timerRef.current) {
        timerRef.current = window.setTimeout(() => {
          setIsDelayed(true);
        }, DELAY_TIMEOUT * 1000);
      }
    }

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [paymentStatus]);

  return isDelayed;
};

export const RetryStripePaymentButton = ({
  stripeAccountId,
  supplierName,
  paymentAmount,
  paymentIntentId,
  returnPath,
  asLink = false,
  linkClassName,
  buttonText,
}: Pick<
  CheckoutSuccessSummaryProps,
  "stripeAccountId" | "paymentIntentId" | "supplierName"
> & {
  paymentAmount: number;
  returnPath?: string;
  asLink?: boolean;
  linkClassName?: string;
  buttonText?: string;
}) => {
  const location = useLocation();
  const [isMakingPayment, setIsMakingPayment] = useState(false);
  const [isPaymentDialogActive, setIsPaymentDialogActive] = useState(false);
  const paymentIntentQuery = usePaymentIntent(paymentIntentId, {
    enabled: !!paymentIntentId,
  });
  const clientSecret = paymentIntentQuery.data?.clientSecret ?? "";
  const [errorMessage, setErrorMessage] = useState("");

  const handlePayment = async ({
    stripe,
    elements,
    clientSecret,
  }: {
    stripe: Stripe;
    elements: StripeElements;
    clientSecret: string;
  }) => {
    setIsMakingPayment(true);
    setErrorMessage("");

    try {
      const submission = await elements.submit();
      if (submission.error) {
        setErrorMessage(
          submission.error.message ?? "Please check your card details.",
        );
        return;
      }

      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: returnPath
            ? `${window.location.origin}${returnPath}`
            : `${import.meta.env.VITE_URL}${location.pathname}`,
        },
      });
      setErrorMessage(
        error.message ?? "We could not process your payment. Please try again.",
      );
    } catch {
      setErrorMessage("We could not process your payment. Please try again.");
    } finally {
      setIsMakingPayment(false);
    }
  };

  const isDisabled =
    isMakingPayment || !stripeAccountId || paymentAmount < 100 || !clientSecret;

  return (
    <>
      {asLink ? (
        <button
          type="button"
          className={twMerge(
            "self-start font-semibold underline",
            linkClassName,
          )}
          onClick={() => setIsPaymentDialogActive(true)}
        >
          Retry payment
        </button>
      ) : (
        <Button
          loading={isMakingPayment}
          color="tertiary"
          style={{
            border: "1px solid #fca5a5",
          }}
          className={"bg-red-100 text-red-800 hover:bg-red-200"}
          onClick={() => setIsPaymentDialogActive(true)}
        >
          {buttonText || "Retry"}
        </Button>
      )}
      <StripeProvider stripeAccountId={stripeAccountId} amount={paymentAmount}>
        <CheckoutDialog
          isOpen={isPaymentDialogActive}
          title={`Make payment to ${supplierName}`}
          description=""
          onDismiss={() => setIsPaymentDialogActive(false)}
        >
          {/* TODO: add existing cards that valid */}
          <div className="flex flex-1 flex-col gap-4">
            {errorMessage && <Alert message={errorMessage} type="error" />}
            <PaymentElement options={{ paymentMethodOrder: ["card"] }} />
            <ElementsConsumer>
              {({ stripe, elements }) => (
                <Button
                  loading={isMakingPayment}
                  disabled={isDisabled}
                  className="w-full"
                  onClick={() => {
                    if (!stripe || !elements) return;
                    handlePayment({ stripe, elements, clientSecret });
                  }}
                >
                  Pay {formatCurrency(paymentAmount)} to {supplierName}
                </Button>
              )}
            </ElementsConsumer>
          </div>
        </CheckoutDialog>
      </StripeProvider>
    </>
  );
};

const RetryCardPaymentWithDepositButton = ({
  stripeAccountId,
  supplierName,
  paymentAmount,
  paymentIntentId,
}: Pick<
  CheckoutSuccessSummaryProps,
  "stripeAccountId" | "paymentIntentId" | "supplierName"
> & {
  paymentAmount: number;
}) => {
  const { business } = useSellarCurrentBusiness();
  const location = useLocation();
  const [isMakingPayment, setIsMakingPayment] = useState(false);
  const [isPaymentDialogActive, setIsPaymentDialogActive] = useState(false);
  const paymentIntentQuery = usePaymentIntent(paymentIntentId, {
    enabled: !!paymentIntentId,
  });
  const clientSecret = paymentIntentQuery.data?.clientSecret ?? "";
  const [errorMessage, setErrorMessage] = useState("");

  const handlePayment = async ({
    stripe,
    elements,
    clientSecret,
  }: {
    stripe: Stripe;
    elements: StripeElements;
    clientSecret: string;
  }) => {
    setIsMakingPayment(true);
    setErrorMessage("");

    // validate stripe elements input values

    const { error } = await elements.submit();
    if (error) {
      setErrorMessage(error.message ?? "");
      return;
    }

    // grab card element

    const cardElement = elements.getElement("card");

    if (!cardElement) {
      setErrorMessage("No card element provided.");
      return;
    }

    // if new card is used, create payment method with card

    const createPaymentMethodResponse = cardElement
      ? await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        })
      : null;

    if (createPaymentMethodResponse?.error) {
      setErrorMessage(createPaymentMethodResponse.error.message ?? "");
      return;
    }

    // confirm card payment

    const receiptEmail = sanitizeEmailArray([
      ...(business?.invoiceEmails ?? []),
      ...(business?.email ? [business.email] : []),
    ])[0];

    const { error: stripeConfirmError } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
        },
        return_url: `${import.meta.env.VITE_URL}${location.pathname}`,
        receipt_email: receiptEmail,
      },
    );

    if (stripeConfirmError) {
      setErrorMessage(
        "We could not process your payment at this time. Please try again later",
      );
      return;
    }

    // if successful, refresh page

    window.location.reload();

    setIsMakingPayment(false);
  };

  const isDisabled =
    isMakingPayment || !stripeAccountId || paymentAmount < 100 || !clientSecret;

  return (
    <>
      <Button
        loading={isMakingPayment}
        color="tertiary"
        style={{
          border: "1px solid #fca5a5",
        }}
        className={"bg-red-100 text-red-800 hover:bg-red-200"}
        onClick={() => setIsPaymentDialogActive(true)}
      >
        Retry
      </Button>
      <StripeProvider stripeAccountId={stripeAccountId} amount={paymentAmount}>
        <CheckoutDialog
          isOpen={isPaymentDialogActive}
          title={`Make payment to ${supplierName}`}
          description=""
          onDismiss={() => setIsPaymentDialogActive(false)}
        >
          <div className="flex flex-1 flex-col gap-4">
            {errorMessage && <Alert message={errorMessage} type="error" />}

            {/* required for setup_future_usage, off_session, stripe.confirmPayment doesn't infer correctly with PaymentElement */}
            <CardElement options={{ hidePostalCode: true }} />

            <ElementsConsumer>
              {({ stripe, elements }) => (
                <Button
                  loading={isMakingPayment}
                  disabled={isDisabled}
                  className="w-full"
                  onClick={() => {
                    if (!stripe || !elements) return;
                    handlePayment({ stripe, elements, clientSecret });
                  }}
                >
                  Pay {formatCurrency(paymentAmount)} to {supplierName}
                </Button>
              )}
            </ElementsConsumer>
          </div>
        </CheckoutDialog>
      </StripeProvider>
    </>
  );
};

export const RetrySellarCreditPaymentButton = ({
  orderId,
}: {
  orderId: number;
}) => {
  const navigate = useNavigate();

  const orderCancelMutation = useOrderCancel();
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  const { toast } = useToast();

  const handleCancelOrder = async ({
    orderId,
    onSuccess,
  }: {
    orderId: number;
    onSuccess: () => void;
  }) => {
    setIsCancellingOrder(true);
    await orderCancelMutation.mutateAsync(
      {
        id: orderId,
        data: {
          cancellationReason: "Could not place order with Sellar Credit",
          cancelledAt: new Date(),
          cancelledBy: "retailer",
          orderComment: null,
        },
      },
      {
        onSuccess,
        onError: () => {
          setIsCancellingOrder(false);
          const message =
            "Could not cancel your order at this time, please try again.";
          toast({
            placement: "bottom",
            render: () => <Toast icon="ALERT">{message}</Toast>,
          });
        },
      },
    );
  };

  const orderUrl = `${ORDERS}/${orderId}`;

  return (
    <PayWithSellarCreditButton
      orderId={orderId}
      renderTrigger={({ loading, disabled, onClick }) => {
        return (
          <Button
            disabled={disabled}
            loading={loading}
            color="tertiary"
            style={{
              border: "1px solid #fca5a5",
            }}
            className="bg-red-100 text-red-800 hover:bg-red-200"
            onClick={onClick}
          >
            Retry
          </Button>
        );
      }}
      cancelOrderButtonProps={{
        loading: isCancellingOrder,
        disabled: isCancellingOrder,
        onClick: (orderId) => {
          handleCancelOrder({
            orderId,
            onSuccess: () => {
              navigate(orderUrl);
            },
          });
        },
      }}
      onSuccess={(response) => {
        if (response.paymentUrl) {
          window.location.replace(response.paymentUrl);
          return;
        }
        window.location.reload();
      }}
      onError={() => {
        // Rely on internal error handling alone
      }}
    />
  );
};

const useTrackEndOfCheckoutFeed = () => {
  const { sessionId } = useMarketStorefrontSessionId();

  const trackFeedContent = ({
    title,
    socialLinkType,
  }: {
    title: string;
    socialLinkType?: string;
  }) => {
    return Mixpanel.track("End Of Checkout Content Clicked", {
      lastSessionId: sessionId,
      title,
      socialLinkType,
    });
  };

  return { trackFeedContent };
};
